import { useState, useEffect, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { JSONFetcher, Button, DSIHtml, LogoSpinner, Modal } from 'shared'

import 'trix/dist/trix.css'

try {
  require('trix')
} catch (err) {
}

export const TrixEditor = (props) => {
  return <>
    <Toolbar {...props} />
    <trix-editor toolbar='trix-toolbar' input={props.hiddenField}></trix-editor>
  </>
}

const Toolbar = (props) => {
  const showMediaButtons = !!props.imagesUrl || !!props.imageUploadUrl
  const withImageGallery = !!props.imagesUrl

  return <trix-toolbar className='trix-toolbar' id='trix-toolbar'>
    <div className="trix-button-row">
      <span className="trix-button-group trix-button-group--text-tools" data-trix-button-group="text-tools">
        <button className="trix-button trix-button--icon trix-button--icon-bold" data-trix-attribute="bold" data-trix-key="b" tabIndex="-1" type="button">
          {I18n.t('modules.trix_editor.button_bold')}
        </button>
        <button className="trix-button trix-button--icon trix-button--icon-italic italic" data-trix-attribute="italic" data-trix-key="i" tabIndex="-1" type="button">
          {I18n.t('modules.trix_editor.button_italic')}
        </button>
        <button className="trix-button trix-button--icon trix-button--icon-bullet-list list bullets" data-trix-attribute="bullet" tabIndex="-1" type="button">
          <span>···</span>
          {I18n.t('modules.trix_editor.button_list')}
        </button>
        <button className="trix-button trix-button--icon trix-button--icon-link link" data-trix-action="link" data-trix-attribute="href" data-trix-key="k" type="button">
          {I18n.t('modules.trix_editor.button_link')}
        </button>
        {showMediaButtons && <>
          <VideosButtonAndModal {...props} />
          {withImageGallery ? <ImagesButtonAndModal {...props} /> : <ImageUploadButton {...props} />}
        </>}
      </span>

      <button data-trix-action="undo" data-trix-key="z" style={{ display: 'none' }} type="button" disabled="">
      </button>
      <button data-trix-action="redo" data-trix-key="shift+z" style={{ display: 'none' }} type="button" disabled="">
      </button>
    </div>

    <div className="trix-dialogs" data-trix-dialogs="">
      <div className="trix-dialog trix-dialog--link" data-trix-dialog="href" data-trix-dialog-attribute="href">
        <div className="trix-dialog__link-fields">
          <input className="trix-input trix-input--dialog" data-trix-input="" name="href" placeholder="URL" required="" type="url" disabled="disabled" />
          <div className="trix-button-group">
            <input className="trix-button trix-button--dialog" data-trix-method="setAttribute" type="button" value={I18n.t('modules.trix_editor.button_add_link')} />
            <input className="trix-button trix-button--dialog" data-trix-method="removeAttribute" type="button" value={I18n.t('modules.trix_editor.button_unlink')} />
          </div>
        </div>
      </div>
    </div>
  </trix-toolbar>
}

const ImagesButtonAndModal = (props) => {
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const hidePhotoModal = () => setShowPhotoModal(false)

  const [images, setImages] = useState([])

  useEffect(() => {
    JSONFetcher.load({ url: props.imagesUrl, success: (data) => setImages(data) })
  }, [])

  const onDrop = useCallback(acceptedFiles => {
    acceptedFiles.forEach((file) => {
      setLoading(true)
      let formData = new FormData()
      formData.append('file', file)

      const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content')

      fetch(props.imagesUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': token },
        body: formData,
      }).then(response => response.json())
        .then((json) => {
          setImages(json)
          setLoading(false)
        })
    })
  }, [])
  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  return <>
    <button className="trix-button" type="button" onClick={() => setShowPhotoModal(true)}>
      {I18n.t('modules.trix_editor.button_picture_insert')}
    </button>

    <Modal
      id='photo-modal'
      onHide={hidePhotoModal}
      show={showPhotoModal}
      header={I18n.t('modules.trix_editor.picture_modal_message.title')}
    >
      <div {...getRootProps()} className='mb-6'>
        <input {...getInputProps()} />
        <Button loading={loading} type='button' className='btn btn-small btn-primary'>{I18n.t('modules.trix_editor.picture_modal_message.choose_files_button')}</Button>
      </div>
      {images ? <p>{I18n.t('modules.trix_editor.picture_modal_message.explanation_html')}</p> : <p className='my-2'>{I18n.t('modules.trix_editor.picture_modal_message.zero_pictures_warning')}</p>}
      <div className='d-flex flex-wrap my-2'>
        {images.map((img, index) => <ImageSelector {...img} key={index} closeModal={hidePhotoModal} />)}
      </div>
    </Modal>
  </>
}

const ImageUploadButton = (props) => {

  const [loading, setLoading] = useState(false)

  const handleChange = (event) => {
    setLoading(true)
    let formData = new FormData()
    formData.append('file', event.target.files[0])

    const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content')

    fetch(props.imageUploadUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': token },
      body: formData,
    })
      .then(response => response.json())
      .then((json) => {
        const trixEditor = document.getElementsByTagName('trix-editor')[0].editor
        trixEditor.insertString(json.url)
        trixEditor.insertLineBreak()
        trixEditor.insertLineBreak()
        setLoading(false)
      })
  }

  const loadingStyle = {
    color: 'transparent',
    textShadow: 'none',
    lineHeight: 0,
    transition: 'none',
  }

  return <>
    <label htmlFor='image-upload' className='trix-button d-flex align-items-center justify-center' style={loading ? loadingStyle : {}}>
      {loading && <LogoSpinner color='#9ECB0A' height={20} style={{ position: 'absolute' }} />}
      {I18n.t('modules.trix_editor.button_picture_insert')}
    </label>
    <input type="file" id='image-upload' multiple={false} style={{ position: 'absolute', top: '-10vh', left: 0 }} onChange={handleChange} />
  </>
}

const ImageSelector = (props) => {
  const handleClick = () => {
    const trixEditor = document.getElementsByTagName('trix-editor')[0].editor
    trixEditor.insertString(props.insert)
    trixEditor.insertLineBreak()
    trixEditor.insertLineBreak()
    props.closeModal()
  }

  return <span className='trix-image-selector' onClick={handleClick}>
    <img src={props.thumb}></img>
  </span>
}

const VideosButtonAndModal = () => {
  const [showVideoModal, setShowVideoModal] = useState(false)
  const hideVideoModal = () => setShowVideoModal(false)

  return <>
    <button className="trix-button" type="button" onClick={() => setShowVideoModal(true)}>
      {I18n.t('modules.trix_editor.button_video_insert')}
    </button>

    <Modal
      id='video-modal'
      onHide={hideVideoModal}
      show={showVideoModal}
      header={I18n.t('modules.trix_editor.video_modal_message.title')}
    >
      <DSIHtml value={I18n.t('modules.trix_editor.video_modal_message.explanation_html')} />
    </Modal>
  </>
}
